/**
 * Follow-Up Review Page
 *
 * Shows complete form data organized by modules (PDF-like preview).
 */

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useFollowUp } from '../context/FollowUpContext';
import { useTenant } from '../context/TenantContext';
import { generatePDF } from '../services/pdfGenerator';
import defaultLogo from '../assets/logo.png';
import logger from '../utils/logger';
import activityLogger from '../utils/activityLogger';
import '../styles/formPage.css';
import '../styles/followup.css';

const REVIEW_STEP = [4, 'Review'];

// Display as "Zip Code" even when the API label says "Postal Code"
const toDisplayLabel = (label) =>
  typeof label === 'string' ? label.replace(/postal\s*code/gi, (m) =>
    m === m.toLowerCase() ? 'zip code' : m === m.toUpperCase() ? 'ZIP CODE' : 'Zip Code') : label;

// Helper to format field names for display - Patient-friendly labels
const formatFieldLabel = (fieldName) => {
  if (!fieldName) return '';

  // Map technical terms to patient-friendly labels
  const friendlyLabels = {
    'firstname': 'First Name',
    'lastname': 'Last Name',
    'dateofbirth': 'Date of Birth',
    'dob': 'Date of Birth',
    'phonenumber': 'Phone Number',
    'phone': 'Phone',
    'email': 'Email Address',
    'postalcode': 'Zip Code',
    'zipcode': 'Zip Code',
    'streetaddress': 'Street Address',
    'tandc': 'Terms Accepted',
    't&c': 'Terms Accepted',
    'dlfront': 'Driver License (Front)',
    'dlback': 'Driver License (Back)',
    'primaryinsfront': 'Insurance Card (Front)',
    'primaryinsback': 'Insurance Card (Back)',
    'secondaryinsfront': 'Secondary Insurance (Front)',
    'secondaryinsback': 'Secondary Insurance (Back)',
    'filesmedicationlist': 'Medications from Upload',
    'cameramedicationlist': 'Medications from Photo',
    'wizardmedicationlist': 'Medications from Med Wizard',
    'voiceinputformatted': 'Medications from Voice',
    'filesallergylist': 'Allergies from Upload',
    'authorizedcontact': 'Emergency Contact',
  };

  const keyLower = fieldName.toLowerCase().replace(/[^a-z0-9]/g, '');

  // Check for exact match first
  if (friendlyLabels[keyLower]) {
    return friendlyLabels[keyLower];
  }

  // Check for partial matches
  for (const [key, label] of Object.entries(friendlyLabels)) {
    if (keyLower.includes(key)) {
      return label;
    }
  }

  // Default formatting
  return fieldName
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();
};

// Same as PDF generator - converts field label to form data key
const toLowercase = (str) => {
  if (!str || typeof str !== 'string') return '';
  return str
    .replace(/[^a-zA-Z0-9 ]/g, '')
    .trim()
    .split(/\s+/)
    .join('')
    .toLowerCase();
};

// Helper to check if value should be hidden
const shouldHideField = (key, value) => {
  if (!key) return true;
  const keyLower = key.toLowerCase();

  if (key.startsWith('_')) return true;
  if (keyLower === 'medicationdata' || keyLower === 'pharmacydata' || keyLower === 'allergiesdata') return true;
  if (keyLower.includes('paindiagram') || keyLower.includes('pain_diagram')) return true;
  if (keyLower.includes('painlevel') || keyLower.includes('pain_level')) return true;
  if (keyLower.includes('base64')) return true;
  if (typeof value === 'string' && value.startsWith('data:')) return true;
  if (value === null || value === undefined) return true;
  if (typeof value === 'string' && !value.trim()) return true;
  if (typeof value === 'object' && !(value instanceof Date) && !Array.isArray(value) && Object.keys(value).length === 0) return true;

  return false;
};

// Helper to check if value is displayable (not a complex object, but Date is OK)
const isDisplayableValue = (value) => {
  if (value === null || value === undefined) return false;
  if (value instanceof Date) return true;
  if (typeof value === 'object' && !Array.isArray(value)) return false;
  if (typeof value === 'string' && value.startsWith('data:')) return false;
  return true;
};

// Helper to format value for display
const formatDisplayValue = (value) => {
  if (value === null || value === undefined) return '-';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  // Handle Date objects - format as MM/DD/YYYY
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return '-';
    const month = (value.getMonth() + 1).toString().padStart(2, '0');
    const day = value.getDate().toString().padStart(2, '0');
    const year = value.getFullYear();
    return `${month}/${day}/${year}`;
  }
  // Handle ISO date strings (from sessionStorage) - format as MM/DD/YYYY
  if (typeof value === 'string') {
    // Check for ISO date format: YYYY-MM-DDTHH:mm:ss.sssZ or YYYY-MM-DD
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?)?$/;
    if (isoDateRegex.test(value)) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
      }
    }
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return '-';
    return value.join(', ');
  }
  if (typeof value === 'object') {
    if (value.pharmacy_name) {
      return `${value.pharmacy_name}, ${value.shop_address || ''}, ${value.city || ''}`;
    }
    return JSON.stringify(value);
  }
  const str = String(value).trim();
  return str || '-';
};

const FollowUpReview = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // "No new issues" mode — hide Update buttons, review-only
  const [readOnlyMode, setReadOnlyMode] = useState(false);
  useEffect(() => {
    if (location.state?.noNewIssues) {
      setReadOnlyMode(true);
    }
  }, [location.state]);

  // Show toast from section edit (once only)
  const toastShownRef = React.useRef(false);
  useEffect(() => {
    if (location.state?.toast && !toastShownRef.current) {
      toastShownRef.current = true;
      toast.success(`\u2713 ${location.state.toast}`, { autoClose: 3000 });
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  const { tenant } = useTenant();
  const {
    isExistingUser,
    isNewUser,
    error,
    submissionInfo,
    availableModules,
    previousData,
    updatedFields,
    submitUpdates,
    generatePDF: generateFollowUpPDFApi,
    setError,
    userEmail,
    allModules,
    fieldsMap,
    getMergedFormData,
  } = useFollowUp();

  // Get the logo to display - use tenant logo if available, otherwise default
  const logoToDisplay = tenant?.logoUrl || defaultLogo;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);

  // Give session storage a moment to be read (runs once on mount)
  useEffect(() => {
    const timer = setTimeout(() => {
      setSessionReady(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // Get merged form data
  const mergedFormData = useMemo(() => {
    const data = getMergedFormData();
    return data;
  }, [getMergedFormData, updatedFields, previousData]);

  // Get changed field keys for highlighting - compare updatedFields with previousData
  const changedFieldKeys = useMemo(() => {
    const changed = new Set();

    // Helper to normalize a key for comparison
    const normalizeKey = (key) => key?.toLowerCase().replace(/[^a-z0-9]/g, '') || '';

    // Helper to normalize a value for comparison
    const normalizeValue = (val) => {
      if (val === undefined || val === null) return '';
      if (typeof val === 'object') return JSON.stringify(val);
      return String(val).trim();
    };

    // Create a normalized lookup of previousData for easier comparison
    const previousNormalized = {};
    Object.entries(previousData || {}).forEach(([key, value]) => {
      const normalizedKey = normalizeKey(key);
      if (normalizedKey) {
        previousNormalized[normalizedKey] = normalizeValue(value);
      }
    });

    // Check all keys in updatedFields
    Object.keys(updatedFields || {}).forEach(key => {
      const keyNormalized = normalizeKey(key);
      const newValue = updatedFields[key];

      // Allow specific keys through for highlight detection even though they're hidden from display
      const isModuleDataKey = keyNormalized === 'medicationdata' || keyNormalized === 'pharmacydata' || keyNormalized === 'allergiesdata';
      const isPainLevelKey = keyNormalized.includes('painlevel');
      if (!isModuleDataKey && !isPainLevelKey && shouldHideField(key, newValue)) return;
      if (key.startsWith('_')) return;
      // Exclude new-issues internal fields from change count
      const internalKeys = ['newissues', 'hasnewissues', 'newissuestimestamp', 'newissuesdescription'];
      if (internalKeys.includes(keyNormalized)) return;

      const newStr = normalizeValue(newValue);

      // Find previous value using normalized key
      const prevStr = previousNormalized[keyNormalized] || '';

      // Only mark as changed if values are actually different
      // AND the new value is not empty (don't highlight empty fields)
      if (prevStr !== newStr && newStr !== '') {
        changed.add(keyNormalized);
      }
    });

    return changed;
  }, [updatedFields, previousData]);

  // Organize data by modules - same approach as PDF generator
  const organizedData = useMemo(() => {
    const modules = allModules || availableModules || [];
    const result = [];
    const usedFormKeys = new Set();

    // Create normalized formData lookup with multiple key formats
    const normalizedFormData = {};
    Object.entries(mergedFormData).forEach(([key, value]) => {
      const keyLower = key.toLowerCase();
      const keyNoSpaces = toLowercase(key);
      const keyNoUnderscores = keyLower.replace(/_/g, '');

      normalizedFormData[keyLower] = { originalKey: key, value };
      normalizedFormData[keyNoSpaces] = { originalKey: key, value };
      normalizedFormData[keyNoUnderscores] = { originalKey: key, value };
    });

    // Process each module
    modules.forEach(mod => {
      const moduleName = mod.module_name || mod.name || '';
      const moduleNameLower = moduleName.toLowerCase();
      const moduleType = mod.module_type || '';

      // Pain diagram - show summary instead of skipping
      if (moduleNameLower.includes('pain diagram')) {
        const hasPainDiagram = mergedFormData.painDiagramPdf || mergedFormData.paindiagrampdf || mergedFormData.painDiagramData || mergedFormData.paindiagramdata;
        result.push({
          moduleName,
          fields: [{
            fieldName: 'paindiagram',
            fieldLabel: 'Pain Diagram',
            value: hasPainDiagram ? 'Pain diagram submitted' : 'No pain diagram',
            isChanged: false
          }]
        });
        return;
      }

      const moduleFields = fieldsMap[moduleName] || [];
      const fieldsWithValues = [];

      // ===== ADDITIONAL INFORMATION - Special handling for Address =====
      if (moduleNameLower.includes('additional information')) {
        // Use fieldsMap if available, otherwise fallback to known fields
        const fieldsToCheck = moduleFields.length > 0 ? moduleFields : [
          { labelName: 'Date of Birth', type: 'date' },
          { labelName: 'Gender', type: 'radio' },
          { labelName: 'Address', type: 'address' },
          { labelName: 'Postal Code', type: 'text' },
        ];

        fieldsToCheck.forEach(field => {
          const fieldLabel = field.labelName || field.label || field.name || field.field_name;
          if (!fieldLabel) return;
          const normalizedKey = toLowercase(fieldLabel);
          const fieldType = field.type || '';

          // Handle address field specially
          if (fieldType === 'address' || fieldLabel.toLowerCase().includes('address')) {
            // Check updatedFields keys first (address_street), then normalized (addressstreet)
            const uf = updatedFields || {};
            let street = uf.address_street || uf.addressstreet || mergedFormData.address_street || mergedFormData.addressstreet || '';
            let suite = uf.address_suite || uf.addresssuite || mergedFormData.address_suite || mergedFormData.addresssuite || '';
            let city = uf.address_city || uf.addresscity || mergedFormData.address_city || mergedFormData.addresscity || '';
            let state = uf.address_state || uf.addressstate || mergedFormData.address_state || mergedFormData.addressstate || '';
            let country = uf.address_country || uf.addresscountry || mergedFormData.address_country || mergedFormData.addresscountry || '';

            const addressChanged = changedFieldKeys.has('addressstreet') || changedFieldKeys.has('addresscity') ||
              changedFieldKeys.has('addressstate') || changedFieldKeys.has('addresssuite') || changedFieldKeys.has('addresscountry');

            const addressParts = [street, suite, city, state, country].filter(p => p && String(p).trim());
            if (addressParts.length > 0) {
              fieldsWithValues.push({
                fieldName: 'address',
                fieldLabel: 'Address',
                value: addressParts.join(', '),
                isChanged: addressChanged
              });
            }
            return;
          }

          let value = mergedFormData[normalizedKey] || mergedFormData[fieldLabel.toLowerCase().replace(/\s+/g, '')];
          if (value && isDisplayableValue(value)) {
            fieldsWithValues.push({ fieldName: normalizedKey, fieldLabel, value: formatDisplayValue(value), isChanged: changedFieldKeys.has(normalizedKey) });
          }
        });

        if (fieldsWithValues.length > 0) result.push({ moduleName, fields: fieldsWithValues });
        return;
      }

      // ===== PATIENT ONBOARDING DETAILS - Skip T & C =====
      if (moduleNameLower.includes('patient onboarding')) {
        // Use fieldsMap if available, otherwise fallback to known common fields
        const fieldsToCheck = moduleFields.length > 0 ? moduleFields : [
          { labelName: 'First Name' }, { labelName: 'Last Name' }, { labelName: 'Email' },
          { labelName: 'Phone Number' }, { labelName: 'Date of Birth' }, { labelName: 'Gender' },
        ];

        fieldsToCheck.forEach(field => {
          const fieldLabel = field.labelName || field.label || field.name || field.field_name;
          if (!fieldLabel) return;
          const normalizedKey = toLowercase(fieldLabel);

          // Skip T & C field
          if (normalizedKey.includes('tandc') || normalizedKey.includes('t&c') || fieldLabel.toLowerCase().includes('t & c') || fieldLabel.toLowerCase().includes('t&c') || fieldLabel.toLowerCase().includes('terms')) {
            return;
          }

          let value = mergedFormData[normalizedKey] || mergedFormData[fieldLabel.toLowerCase().replace(/\s+/g, '')];
          if (value && isDisplayableValue(value)) {
            fieldsWithValues.push({ fieldName: normalizedKey, fieldLabel, value: formatDisplayValue(value), isChanged: changedFieldKeys.has(normalizedKey) });
          }
        });

        if (fieldsWithValues.length > 0) result.push({ moduleName, fields: fieldsWithValues });
        return;
      }

      // ===== CHIEF COMPLAINT =====
      if (moduleNameLower.includes('chief complaint')) {
        moduleFields.forEach(field => {
          const fieldLabel = field.labelName || field.label || field.name || field.field_name;
          if (!fieldLabel) return;
          const normalizedKey = toLowercase(fieldLabel);
          if (normalizedKey.includes('painlevel')) return;

          let value = mergedFormData[normalizedKey] || mergedFormData[fieldLabel.toLowerCase().replace(/\s+/g, '')];
          if (value && isDisplayableValue(value)) {
            fieldsWithValues.push({ fieldName: normalizedKey, fieldLabel, value: formatDisplayValue(value), isChanged: changedFieldKeys.has(normalizedKey) });
          }
        });
        if (fieldsWithValues.length > 0) result.push({ moduleName, fields: fieldsWithValues });
        return;
      }

      // ===== HISTORY OF PRESENT ILLNESS - Pain Levels =====
      if (moduleNameLower.includes('history of present illness')) {
        const currentPain = mergedFormData.currentpainlevel ?? mergedFormData.currentPainLevel;
        const worstPain = mergedFormData.worstpainlevel ?? mergedFormData.worstPainLevel;
        const bestPain = mergedFormData.bestpainlevel ?? mergedFormData.bestPainLevel;

        // Use != null to allow 0 as valid value
        if (currentPain != null && currentPain !== '') fieldsWithValues.push({ fieldName: 'currentpainlevel', fieldLabel: 'Current Pain Level', value: `${currentPain}/10`, isChanged: changedFieldKeys.has('currentpainlevel') });
        if (worstPain != null && worstPain !== '') fieldsWithValues.push({ fieldName: 'worstpainlevel', fieldLabel: 'Worst Pain Level', value: `${worstPain}/10`, isChanged: changedFieldKeys.has('worstpainlevel') });
        if (bestPain != null && bestPain !== '') fieldsWithValues.push({ fieldName: 'bestpainlevel', fieldLabel: 'Best Pain Level', value: `${bestPain}/10`, isChanged: changedFieldKeys.has('bestpainlevel') });

        // Also add other HPI fields from fieldsMap or fallback
        const hpiFields = moduleFields.length > 0 ? moduleFields : [
          { labelName: 'How did your current problem start?' },
          { labelName: 'How long have you experienced the problem?' },
          { labelName: 'Describe the trend of your symptoms' },
          { labelName: 'Describe the frequency of your symptoms or how often they occur' },
          { labelName: 'Over the last week, how have your symptoms progressed?' },
          { labelName: 'Over the last month, how have your symptoms progressed?' },
          { labelName: 'Activities that make the problem worse' },
          { labelName: 'Activities that make the problem better' },
          { labelName: 'How long can you sit in place without the need to stand?' },
        ];

        hpiFields.forEach(field => {
          const fieldLabel = field.labelName || field.label || field.name || field.field_name;
          if (!fieldLabel) return;
          const normalizedKey = toLowercase(fieldLabel);
          if (normalizedKey.includes('painlevel')) return;

          let value = mergedFormData[normalizedKey] || mergedFormData[fieldLabel.toLowerCase().replace(/\s+/g, '')];
          if (value && isDisplayableValue(value)) {
            fieldsWithValues.push({ fieldName: normalizedKey, fieldLabel, value: formatDisplayValue(value), isChanged: changedFieldKeys.has(normalizedKey) });
          }
        });
        if (fieldsWithValues.length > 0) result.push({ moduleName, fields: fieldsWithValues });
        return;
      }

      // ===== MEDICATION ENTRY =====
      if (moduleType === 'medication' || moduleNameLower.includes('medication entry')) {
        // Per-field highlight: only highlight the specific field that actually changed
        const isFieldChanged = (key) => changedFieldKeys.has(key);

        const filesMeds = mergedFormData.medicationlistfromfiles || mergedFormData.filesMedicationList || mergedFormData.filesmedicationlist;
        const cameraMeds = mergedFormData.medicationlistfromcamera || mergedFormData.cameraMedicationList || mergedFormData.cameramedicationlist;
        const wizardMeds = mergedFormData.medicationlistfromwizard || mergedFormData.wizardMedicationList || mergedFormData.wizardmedicationlist;
        const voiceMeds = mergedFormData.medicationvoiceinputlist || mergedFormData.voiceInputFormatted || mergedFormData.voiceinputformatted;

        if (filesMeds) fieldsWithValues.push({ fieldName: 'medicationlistfromfiles', fieldLabel: 'Medications (Upload)', value: String(filesMeds), isChanged: isFieldChanged('medicationlistfromfiles') });
        if (cameraMeds) fieldsWithValues.push({ fieldName: 'medicationlistfromcamera', fieldLabel: 'Medications (Camera)', value: String(cameraMeds), isChanged: isFieldChanged('medicationlistfromcamera') });
        if (wizardMeds) fieldsWithValues.push({ fieldName: 'medicationlistfromwizard', fieldLabel: 'Medications (Med Wizard)', value: String(wizardMeds), isChanged: isFieldChanged('medicationlistfromwizard') });
        if (voiceMeds) fieldsWithValues.push({ fieldName: 'medicationvoiceinputlist', fieldLabel: 'Medications (Voice/Search)', value: String(voiceMeds), isChanged: isFieldChanged('medicationvoiceinputlist') });

        // Manual medications - check all possible key variations
        const manualMedList = mergedFormData.manualmedicationlist || mergedFormData.manualMedicationList ||
          (updatedFields || {}).manualmedicationlist || (updatedFields || {}).manualMedicationList;
        const manualMedArr = mergedFormData.manualMedications || mergedFormData.manualmedications ||
          (updatedFields || {}).manualMedications || (updatedFields || {}).manualmedications;
        const manualMedChanged = isFieldChanged('manualmedicationlist') || isFieldChanged('manualmedications') || isFieldChanged('manualmedications');
        if (manualMedList) {
          fieldsWithValues.push({ fieldName: 'manualmedicationlist', fieldLabel: 'Medications (Manually Added)', value: String(manualMedList), isChanged: manualMedChanged });
        } else if (manualMedArr && Array.isArray(manualMedArr) && manualMedArr.length > 0) {
          const formatted = manualMedArr.map((med, i) => `${i + 1}. ${[med.type, med.generic, med.brand, med.Generic, med.Brand].filter(Boolean).join(' - ')}`).join(' | ');
          fieldsWithValues.push({ fieldName: 'manualmedicationlist', fieldLabel: 'Medications (Manually Added)', value: formatted, isChanged: manualMedChanged });
        }

        // Fallback to nested medicationData
        const medData = mergedFormData.medicationdata || mergedFormData.medicationData;
        if (medData && fieldsWithValues.length === 0) {
          if (medData.filesMedicationList) fieldsWithValues.push({ fieldName: 'filesMedicationList', fieldLabel: 'Medications (Upload)', value: String(medData.filesMedicationList), isChanged: isFieldChanged('filesmedicationlist') });
          if (medData.cameraMedicationList) fieldsWithValues.push({ fieldName: 'cameraMedicationList', fieldLabel: 'Medications (Camera)', value: String(medData.cameraMedicationList), isChanged: isFieldChanged('cameramedicationlist') });
          if (medData.wizardMedicationList) fieldsWithValues.push({ fieldName: 'wizardMedicationList', fieldLabel: 'Medications (Med Wizard)', value: String(medData.wizardMedicationList), isChanged: isFieldChanged('wizardmedicationlist') });
          if (medData.voiceInputFormatted) fieldsWithValues.push({ fieldName: 'voiceInputFormatted', fieldLabel: 'Medications (Voice)', value: String(medData.voiceInputFormatted), isChanged: isFieldChanged('voiceinputformatted') });
        }

        if (fieldsWithValues.length === 0) fieldsWithValues.push({ fieldName: 'medications', fieldLabel: 'Medications', value: 'No medications recorded', isChanged: false });
        result.push({ moduleName, fields: fieldsWithValues });
        return;
      }

      // ===== MEDICATION ALLERGIES =====
      if (moduleType === 'medication_allergies' || moduleNameLower.includes('allerg')) {
        // Per-field highlight: only highlight the specific field that actually changed
        const isAllergyFieldChanged = (key) => changedFieldKeys.has(key);

        const filesAllergies = mergedFormData.allergieslistfromfiles || mergedFormData.filesAllergyList || mergedFormData.filesallergylist;
        const voiceAllergies = mergedFormData.allergiesvoiceinputlist || mergedFormData.voiceInputFormatted || mergedFormData.voiceinputformatted;

        if (filesAllergies) fieldsWithValues.push({ fieldName: 'allergieslistfromfiles', fieldLabel: 'Allergies (Upload)', value: String(filesAllergies), isChanged: isAllergyFieldChanged('allergieslistfromfiles') });
        if (voiceAllergies) fieldsWithValues.push({ fieldName: 'allergiesvoiceinputlist', fieldLabel: 'Allergies (Voice/Search)', value: String(voiceAllergies), isChanged: isAllergyFieldChanged('allergiesvoiceinputlist') });

        // Manual allergies - check all possible key variations
        const manualAlrgList = mergedFormData.manualallergieslist || mergedFormData.manualAllergiesList ||
          (updatedFields || {}).manualallergieslist || (updatedFields || {}).manualAllergiesList;
        const manualAlrgArr = mergedFormData.manualAllergies || mergedFormData.manualallergies ||
          (updatedFields || {}).manualAllergies || (updatedFields || {}).manualallergies;
        const manualAlrgChanged = isAllergyFieldChanged('manualallergieslist') || isAllergyFieldChanged('manualallergies');
        if (manualAlrgList) {
          fieldsWithValues.push({ fieldName: 'manualallergieslist', fieldLabel: 'Allergies (Manually Added)', value: String(manualAlrgList), isChanged: manualAlrgChanged });
        } else if (manualAlrgArr && Array.isArray(manualAlrgArr) && manualAlrgArr.length > 0) {
          const formatted = manualAlrgArr.map((med, i) => `${i + 1}. ${[med.type, med.generic, med.brand, med.Generic, med.Brand].filter(Boolean).join(' - ')}`).join(' | ');
          fieldsWithValues.push({ fieldName: 'manualallergieslist', fieldLabel: 'Allergies (Manually Added)', value: formatted, isChanged: manualAlrgChanged });
        }

        // Fallback to nested allergiesData
        const allergyData = mergedFormData.allergiesdata || mergedFormData.allergiesData;
        if (allergyData && fieldsWithValues.length === 0) {
          if (allergyData.filesMedicationList) fieldsWithValues.push({ fieldName: 'filesMedicationList', fieldLabel: 'Allergies (Upload)', value: String(allergyData.filesMedicationList), isChanged: isAllergyFieldChanged('filesmedicationlist') });
          if (allergyData.voiceInputFormatted) fieldsWithValues.push({ fieldName: 'voiceInputFormatted', fieldLabel: 'Allergies (Voice)', value: String(allergyData.voiceInputFormatted), isChanged: isAllergyFieldChanged('voiceinputformatted') });
        }

        if (fieldsWithValues.length === 0) fieldsWithValues.push({ fieldName: 'allergies', fieldLabel: 'Allergies', value: 'No allergies recorded', isChanged: false });
        result.push({ moduleName, fields: fieldsWithValues });
        return;
      }

      // ===== PHARMACY =====
      if (moduleType === 'pharmacy' || moduleNameLower.includes('pharmacy')) {
        const isPharmacyChanged = changedFieldKeys.has('pharmacydata') || changedFieldKeys.has('selectedpharmacy') ||
          changedFieldKeys.has('pharmacyname');

        const pharmacyData = mergedFormData.pharmacydata || mergedFormData.pharmacyData;
        if (pharmacyData?.selectedPharmacy) {
          const p = pharmacyData.selectedPharmacy;
          fieldsWithValues.push({ fieldName: 'pharmacyName', fieldLabel: 'Pharmacy Name', value: p.pharmacy_name || p.name || 'N/A', isChanged: isPharmacyChanged });
          if (p.shop_address || p.address) fieldsWithValues.push({ fieldName: 'address', fieldLabel: 'Address', value: p.shop_address || p.address, isChanged: isPharmacyChanged });
          if (p.city) fieldsWithValues.push({ fieldName: 'city', fieldLabel: 'City', value: p.city, isChanged: isPharmacyChanged });
          if (p.state) fieldsWithValues.push({ fieldName: 'state', fieldLabel: 'State', value: p.state, isChanged: isPharmacyChanged });
          if (p.phone) fieldsWithValues.push({ fieldName: 'phone', fieldLabel: 'Phone', value: p.phone, isChanged: isPharmacyChanged });
          if (p.fax) fieldsWithValues.push({ fieldName: 'fax', fieldLabel: 'Fax', value: p.fax, isChanged: isPharmacyChanged });
        } else {
          fieldsWithValues.push({ fieldName: 'pharmacy', fieldLabel: 'Pharmacy', value: 'No pharmacy selected', isChanged: false });
        }
        result.push({ moduleName, fields: fieldsWithValues });
        return;
      }

      // ===== PAST MEDICAL HISTORY & ROS =====
      if (moduleNameLower.includes('past medical history') || moduleNameLower.includes('review of systems')) {
        const addedQuestions = new Set();
        const normalizeForCheck = (k) => k?.toLowerCase().replace(/[^a-z0-9]/g, '') || '';

        // Detect if user edited this accordion section
        const isAccordionEdited = moduleFields.some(f => {
          if (f.type !== 'accordion') return false;
          const baseName = (f.field_name || f.name || f.labelName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
          return Object.keys(updatedFields || {}).some(k => {
            const kNorm = k.toLowerCase().replace(/[^a-z0-9]/g, '');
            return kNorm.startsWith(baseName) && kNorm.includes('section');
          });
        });

        // Build map of previous section values for per-section change detection
        const prevSectionValues = {};
        Object.values(previousData || {}).forEach(val => {
          if (val && typeof val === 'object' && val.question && val.selectedValues) {
            prevSectionValues[val.question] = val.selectedValues.slice().sort().join(',');
          }
        });

        // When edited, use raw checkbox data (Method 3) for accurate current values
        // When not edited, use processed data (Methods 1/2) from backend
        if (!isAccordionEdited) {
          // Method 1: Pre-processed accordion data
          Object.keys(mergedFormData).forEach(key => {
            if (key.startsWith('accordion_') || key.includes('accordion')) {
              const accordionData = mergedFormData[key];
              if (accordionData && typeof accordionData === 'object') {
                const question = accordionData.question || key;
                const selectedValues = accordionData.selectedValues || [];
                if (!addedQuestions.has(question) && selectedValues.length > 0) {
                  addedQuestions.add(question);
                  fieldsWithValues.push({ fieldName: key, fieldLabel: question, value: selectedValues.join(', '), isChanged: false });
                }
              }
            }
          });

          // Method 2: Aggregated section data
          Object.keys(mergedFormData).forEach(key => {
            const val = mergedFormData[key];
            if (val && typeof val === 'object' && val.question && val.selectedValues && Array.isArray(val.selectedValues) && val.selectedValues.length > 0) {
              if (!addedQuestions.has(val.question)) {
                addedQuestions.add(val.question);
                fieldsWithValues.push({ fieldName: key, fieldLabel: val.question, value: val.selectedValues.join(', '), isChanged: false });
              }
            }
          });
        }

        // Method 3: Raw checkbox data — runs when edited OR as fallback
        if (isAccordionEdited || (fieldsWithValues.length === 0 && moduleFields.length > 0)) {
          moduleFields.forEach(field => {
            if (field.type === 'accordion' && field.accordionSections) {
              const baseFieldName = toLowercase(field.field_name || field.name || field.labelName || '');

              field.accordionSections.forEach((section, sectionIndex) => {
                const sectionFieldName = `${baseFieldName}_section_${sectionIndex}`;
                const selectedValues = [];

                section.checkboxes?.forEach((checkbox, checkboxIndex) => {
                  const checkboxKey = `${sectionFieldName}_checkbox_${checkboxIndex}`;
                  if (mergedFormData[checkboxKey] === true || mergedFormData[checkboxKey] === 'true') {
                    selectedValues.push(checkbox.label);
                  }
                });

                if (selectedValues.length > 0 && !addedQuestions.has(section.title)) {
                  addedQuestions.add(section.title);
                  // Per-section highlight: compare current values with previous
                  const currentStr = selectedValues.slice().sort().join(',');
                  const prevStr = prevSectionValues[section.title] || '';
                  const sectionChanged = currentStr !== prevStr;

                  fieldsWithValues.push({
                    fieldName: `${baseFieldName}_${sectionIndex}`,
                    fieldLabel: section.title,
                    value: selectedValues.join(', '),
                    isChanged: sectionChanged
                  });
                }
              });
            }
          });
        }

        if (fieldsWithValues.length === 0) fieldsWithValues.push({ fieldName: 'history', fieldLabel: 'Medical History', value: 'No data recorded', isChanged: false });
        result.push({ moduleName, fields: fieldsWithValues });
        return;
      }

      // ===== ID VERIFICATION - Show Image Links =====
      if (moduleNameLower.includes('id verification') || (moduleNameLower.includes('verification') && !moduleNameLower.includes('otp'))) {
        const idFields = [
          { keys: ['dlfront', 'dlFront', 'dl_front', 'driverLicenseFront'], label: 'Driver License (Front)' },
          { keys: ['dlback', 'dlBack', 'dl_back', 'driverLicenseBack'], label: 'Driver License (Back)' },
          { keys: ['primaryinsfront', 'primaryInsFront', 'primary_ins_front', 'primaryInsuranceFront'], label: 'Insurance Card (Front)' },
          { keys: ['primaryinsback', 'primaryInsBack', 'primary_ins_back', 'primaryInsuranceBack'], label: 'Insurance Card (Back)' },
          { keys: ['secondaryinsfront', 'secondaryInsFront', 'secondary_ins_front', 'secondaryInsuranceFront'], label: 'Secondary Insurance (Front)' },
          { keys: ['secondaryinsback', 'secondaryInsBack', 'secondary_ins_back', 'secondaryInsuranceBack'], label: 'Secondary Insurance (Back)' },
          { keys: ['tertiaryinsfront', 'tertiaryInsFront', 'tertiary_ins_front', 'tertiaryInsuranceFront'], label: 'Tertiary Insurance (Front)' },
          { keys: ['tertiaryinsback', 'tertiaryInsBack', 'tertiary_ins_back', 'tertiaryInsuranceBack'], label: 'Tertiary Insurance (Back)' }
        ];

        const uploadedImages = mergedFormData._uploadedImages || {};
        const processedImages = mergedFormData._processedImages || {};

        const resolveUrl = (val) => {
          if (!val) return null;
          if (typeof val === 'string' && val.startsWith('data:')) return { url: val, isBase64: true };
          if (typeof val === 'string' && (val.includes('http') || val.startsWith('/'))) return { url: val, isBase64: false };
          if (typeof val === 'object' && !Array.isArray(val) && val.preview) return { url: val.preview, isBase64: val.preview.startsWith('data:') };
          if (typeof val === 'object' && !Array.isArray(val) && val.url) return { url: val.url, isBase64: false };
          if (Array.isArray(val) && val[0]?.url) return { url: val[0].url, isBase64: false };
          return null;
        };

        idFields.forEach(({ keys, label }) => {
          let resolved = null;
          for (const key of keys) {
            resolved = resolveUrl(mergedFormData[key]) || resolveUrl(mergedFormData[key.toLowerCase()]);
            if (resolved) break;
            resolved = resolveUrl(uploadedImages[key]) || resolveUrl(uploadedImages[key.toLowerCase()]);
            if (resolved) break;
            resolved = resolveUrl(processedImages[key]) || resolveUrl(processedImages[key.toLowerCase()]);
            if (resolved) break;
            const gdKey = `${key}_googleDrive`;
            if (mergedFormData[gdKey]) { resolved = { url: mergedFormData[gdKey], isBase64: false }; break; }
          }

          if (resolved) {
            fieldsWithValues.push({
              fieldName: keys[0],
              fieldLabel: label,
              value: resolved.isBase64 ? 'Uploaded' : resolved.url,
              isChanged: changedFieldKeys.has(keys[0]) || keys.some(k => changedFieldKeys.has(k)),
              isLink: !resolved.isBase64,
              isBase64Image: resolved.isBase64,
              base64Src: resolved.isBase64 ? resolved.url : null
            });
          }
        });

        if (fieldsWithValues.length === 0) fieldsWithValues.push({ fieldName: 'verification', fieldLabel: 'ID Documents', value: 'No documents uploaded', isChanged: false });
        result.push({ moduleName, fields: fieldsWithValues });
        return;
      }

      // ===== AUTHORIZED CONTACTS =====
      if (moduleNameLower.includes('authorized contact') || moduleNameLower.includes('emergency contact') || moduleNameLower.includes('additional contact') || moduleNameLower.includes('contact')) {
        const contactsMap = {};
        let contactsFromArray = false;

        // Check updatedFields first for array format (freshest data from section edit)
        const uf = updatedFields || {};
        const allData = { ...mergedFormData, ...uf };
        const contactArrayKeys = Object.keys(allData).filter(k => {
          const v = allData[k];
          return Array.isArray(v) && v.length > 0 && v[0]?.firstName !== undefined;
        });

        if (contactArrayKeys.length > 0) {
          contactsFromArray = true;
          contactArrayKeys.forEach(key => {
            // Prefer updatedFields array over mergedFormData
            const contacts = uf[key] || mergedFormData[key];
            if (!Array.isArray(contacts)) return;
            contacts.forEach((contact, idx) => {
              if (!contact || (!contact.firstName && !contact.lastName && !contact.email && !contact.phone)) return;
              const num = String(idx + 1);
              contactsMap[num] = {
                firstName: contact.firstName || '',
                lastName: contact.lastName || '',
                email: contact.email || '',
                phone: contact.phone || '',
                relationship: contact.relationship || '',
                isEmergencyContact: contact.isEmergencyContact ? 'Yes' : '',
                isAuthorizedContact: contact.isAuthorizedMedical ? 'Yes' : '',
              };
            });
          });
        }

        // Only use flattened format if array format not found (prevents old data overwriting new)
        if (!contactsFromArray) {
          Object.keys(mergedFormData).forEach(key => {
            const keyLower = key.toLowerCase();
            if (keyLower.includes('authorizedcontact') || keyLower.includes('emergencycontact')) {
              const match = keyLower.match(/contact(\d+)/);
              if (match) {
                const contactNum = match[1];
                if (!contactsMap[contactNum]) contactsMap[contactNum] = {};
                const value = mergedFormData[key];
                if (value && typeof value === 'string' && value.trim()) {
                  if (keyLower.includes('firstname') || keyLower.includes('first_name')) contactsMap[contactNum].firstName = value;
                  else if (keyLower.includes('lastname') || keyLower.includes('last_name')) contactsMap[contactNum].lastName = value;
                  else if (keyLower.includes('phone')) contactsMap[contactNum].phone = value;
                  else if (keyLower.includes('email')) contactsMap[contactNum].email = value;
                  else if (keyLower.includes('emergencyrelationship') || keyLower.includes('emergency_relationship')) contactsMap[contactNum].emergencyRelationship = value;
                  else if (keyLower.includes('emergencycontact') && !keyLower.includes('firstname') && !keyLower.includes('lastname') && !keyLower.includes('phone') && !keyLower.includes('email') && !keyLower.includes('relationship')) contactsMap[contactNum].isEmergencyContact = value;
                  else if (keyLower.includes('authorizedcontact') && !keyLower.includes('firstname') && !keyLower.includes('lastname') && !keyLower.includes('phone') && !keyLower.includes('email') && !keyLower.includes('relationship')) contactsMap[contactNum].isAuthorizedContact = value;
                  else if (keyLower.includes('relationship')) contactsMap[contactNum].relationship = value;
                }
              }
            }
          });
        }

        // Build previous contacts using SAME logic as current, but from previousData only
        const prevContactsMap = {};
        if (previousData) {
          // 1. Array format from previousData
          Object.values(previousData).forEach(v => {
            if (Array.isArray(v) && v.length > 0 && v[0]?.firstName !== undefined) {
              v.forEach((c, idx) => {
                if (!c || (!c.firstName && !c.lastName && !c.email && !c.phone)) return;
                prevContactsMap[String(idx + 1)] = {
                  firstName: c.firstName || '', lastName: c.lastName || '',
                  email: c.email || '', phone: c.phone || '', relationship: c.relationship || '',
                };
              });
            }
          });
          // 2. Flattened keys (only if array not found)
          if (Object.keys(prevContactsMap).length === 0) {
            Object.keys(previousData).forEach(key => {
              const kl = key.toLowerCase();
              if (kl.includes('authorizedcontact') || kl.includes('emergencycontact')) {
                const m = kl.match(/contact(\d+)/);
                if (m) {
                  const n = m[1];
                  if (!prevContactsMap[n]) prevContactsMap[n] = { firstName: '', lastName: '', email: '', phone: '', relationship: '' };
                  const v = String(previousData[key] || '').trim();
                  if (v) {
                    if (kl.includes('firstname')) prevContactsMap[n].firstName = v;
                    else if (kl.includes('lastname')) prevContactsMap[n].lastName = v;
                    else if (kl.includes('phone')) prevContactsMap[n].phone = v;
                    else if (kl.includes('email')) prevContactsMap[n].email = v;
                    else if (kl.includes('relationship') && !kl.includes('emergency')) prevContactsMap[n].relationship = v;
                  }
                }
              }
            });
          }
        }

        // Build fields for each contact — per-FIELD highlight
        const isNew = (contactNum) => !prevContactsMap[contactNum];
        const fieldChanged = (contactNum, field) => {
          const prev = prevContactsMap[contactNum];
          if (!prev) return true; // new contact
          return (contactsMap[contactNum][field] || '').trim() !== (prev[field] || '').trim();
        };

        Object.keys(contactsMap).sort().forEach(contactNum => {
          const contact = contactsMap[contactNum];

          fieldsWithValues.push({ fieldName: `contact${contactNum}header`, fieldLabel: `--- Contact ${contactNum} ---`, value: '', isChanged: false, isHeader: true });

          if (contact.firstName) fieldsWithValues.push({ fieldName: `contact${contactNum}firstname`, fieldLabel: 'First Name', value: contact.firstName, isChanged: fieldChanged(contactNum, 'firstName') });
          if (contact.lastName) fieldsWithValues.push({ fieldName: `contact${contactNum}lastname`, fieldLabel: 'Last Name', value: contact.lastName, isChanged: fieldChanged(contactNum, 'lastName') });
          if (contact.email) fieldsWithValues.push({ fieldName: `contact${contactNum}email`, fieldLabel: 'Email Address', value: contact.email, isChanged: fieldChanged(contactNum, 'email') });
          if (contact.phone) fieldsWithValues.push({ fieldName: `contact${contactNum}phone`, fieldLabel: 'Phone', value: contact.phone, isChanged: fieldChanged(contactNum, 'phone') });
          if (contact.emergencyRelationship) fieldsWithValues.push({ fieldName: `contact${contactNum}emergrel`, fieldLabel: 'Emergency Relationship', value: contact.emergencyRelationship, isChanged: isNew(contactNum) });
          if (contact.relationship) fieldsWithValues.push({ fieldName: `contact${contactNum}rel`, fieldLabel: 'Relationship', value: contact.relationship, isChanged: fieldChanged(contactNum, 'relationship') });
          if (contact.isEmergencyContact) fieldsWithValues.push({ fieldName: `contact${contactNum}isemerg`, fieldLabel: 'Emergency Contact', value: contact.isEmergencyContact, isChanged: isNew(contactNum) });
          if (contact.isAuthorizedContact) fieldsWithValues.push({ fieldName: `contact${contactNum}isauth`, fieldLabel: 'Authorized Contact', value: contact.isAuthorizedContact, isChanged: isNew(contactNum) });
        });

        if (fieldsWithValues.length > 0) {
          result.push({ moduleName, fields: fieldsWithValues });
          // Mark these fields as used to prevent duplicate section
          Object.keys(mergedFormData).forEach(key => {
            const keyLower = key.toLowerCase();
            if (keyLower.includes('authorizedcontact') || keyLower.includes('emergencycontact')) {
              usedFormKeys.add(keyLower);
            }
          });
        }
        return;
      }

      moduleFields.forEach(field => {
        // Get field key using same approach as PDF generator
        const fieldLabelName = field.labelName || field.label || field.field_name || field.name || '';
        const normalizedFieldKey = toLowercase(fieldLabelName);
        const fieldType = field.type || '';

        if (!normalizedFieldKey) return;

        // Handle address fields - combine street, city, state, zip
        if (fieldType === 'address') {
          // Try multiple key formats for address components
          const baseKey = normalizedFieldKey;
          const possibleBases = [baseKey, 'address', 'streetaddress'];

          let street = '', suite = '', city = '', state = '', zip = '';
          let foundKeys = [];

          for (const base of possibleBases) {
            const streetKey = `${base}_street`;
            const suiteKey = `${base}_suite`;
            const cityKey = `${base}_city`;
            const stateKey = `${base}_state`;
            const zipKey = `${base}_zip`;

            const s = normalizedFormData[streetKey]?.value || mergedFormData[streetKey] || '';
            const su = normalizedFormData[suiteKey]?.value || mergedFormData[suiteKey] || '';
            const c = normalizedFormData[cityKey]?.value || mergedFormData[cityKey] || '';
            const st = normalizedFormData[stateKey]?.value || mergedFormData[stateKey] || '';
            const z = normalizedFormData[zipKey]?.value || mergedFormData[zipKey] || '';

            if (s || su || c || st || z) {
              street = s; suite = su; city = c; state = st; zip = z;
              foundKeys = [streetKey, suiteKey, cityKey, stateKey, zipKey, `${base}_country`];
              break;
            }
          }

          const addressParts = [street, suite, city, state, zip].filter(p => p && String(p).trim());
          const addressValue = addressParts.join(', ');

          if (addressValue) {
            // Mark address component keys as used
            foundKeys.forEach(k => {
              usedFormKeys.add(k);
              usedFormKeys.add(k.toLowerCase());
            });

            // Check if any address component was changed - use consistent normalization
            const isChanged = foundKeys.some(k => {
              const keyNormalized = k.toLowerCase().replace(/[^a-z0-9]/g, '');
              return changedFieldKeys.has(keyNormalized);
            });

            fieldsWithValues.push({
              fieldName: normalizedFieldKey,
              fieldLabel: fieldLabelName,
              value: addressValue,
              isChanged
            });
          }
          return;
        }

        // Find value in formData - try multiple key formats
        let matchedData = normalizedFormData[normalizedFieldKey];
        let value = matchedData?.value;
        let originalKey = matchedData?.originalKey;

        // Try alternative key formats if not found
        if (value === undefined || value === null || value === '') {
          // Try with underscores
          const underscoreKey = fieldLabelName.toLowerCase().replace(/\s+/g, '_');
          matchedData = normalizedFormData[underscoreKey];
          value = matchedData?.value;
          originalKey = matchedData?.originalKey;
        }

        // Try direct lookup in mergedFormData with various formats
        if (value === undefined || value === null || value === '') {
          // Try multiple key variations
          const keyVariations = [
            normalizedFieldKey,
            fieldLabelName.toLowerCase(),
            fieldLabelName.toLowerCase().replace(/\s+/g, '_'),
            fieldLabelName.toLowerCase().replace(/\s+/g, ''),
            fieldLabelName.replace(/\s+/g, '').toLowerCase(),
          ];

          for (const keyVar of keyVariations) {
            if (mergedFormData[keyVar] !== undefined && mergedFormData[keyVar] !== null && mergedFormData[keyVar] !== '') {
              value = mergedFormData[keyVar];
              originalKey = keyVar;
              break;
            }
          }
        }

        // Special handling for Date of Birth field
        const isDateOfBirthField = normalizedFieldKey.includes('dateofbirth') ||
                                   normalizedFieldKey.includes('dob') ||
                                   normalizedFieldKey.includes('birth') ||
                                   fieldLabelName.toLowerCase().includes('date of birth') ||
                                   fieldLabelName.toLowerCase().includes('dob') ||
                                   fieldLabelName.toLowerCase().includes('birth') ||
                                   (fieldType === 'date' && (normalizedFieldKey.includes('date') || fieldLabelName.toLowerCase().includes('birth')));

        if ((value === undefined || value === null || value === '') && isDateOfBirthField) {
          // Try all possible date of birth keys - expanded list
          const dobKeys = [
            'dateofbirth', 'date_of_birth', 'dob', 'DOB', 'DateOfBirth', 'date of birth',
            'birthdate', 'birth_date', 'birthday', 'birth', 'patientdob', 'patient_dob',
            'dateofbirthtext', 'date_of_birth_text', 'dobtext'
          ];
          for (const dobKey of dobKeys) {
            const val = mergedFormData[dobKey] || mergedFormData[dobKey.toLowerCase()];
            if (val !== undefined && val !== null && val !== '') {
              value = val;
              originalKey = dobKey;
              break;
            }
          }
        }

        // If still no value found for date field, search all keys
        if ((value === undefined || value === null || value === '') && isDateOfBirthField) {
          for (const [formKey, formValue] of Object.entries(mergedFormData)) {
            const formKeyLower = formKey.toLowerCase();
            if ((formKeyLower.includes('dob') || formKeyLower.includes('birth') || formKeyLower.includes('dateofbirth')) &&
                formValue !== undefined && formValue !== null && formValue !== '') {
              value = formValue;
              originalKey = formKey;
              break;
            }
          }
        }

        // Final attempt: check if this is a date type field and search for ANY date-like value with matching normalized key
        if ((value === undefined || value === null || value === '') && fieldType === 'date') {
          const searchKey = normalizedFieldKey;
          for (const [formKey, formValue] of Object.entries(mergedFormData)) {
            const formKeyNormalized = formKey.toLowerCase().replace(/[^a-z0-9]/g, '');
            if (formKeyNormalized.includes(searchKey) || searchKey.includes(formKeyNormalized)) {
              if (formValue !== undefined && formValue !== null && formValue !== '') {
                value = formValue;
                originalKey = formKey;
                break;
              }
            }
          }
        }

        // Search through all form data keys for partial match as last resort
        if (value === undefined || value === null || value === '') {
          const searchKey = normalizedFieldKey.replace(/[^a-z0-9]/g, '');
          for (const [formKey, formValue] of Object.entries(mergedFormData)) {
            const normalizedFormKey = formKey.toLowerCase().replace(/[^a-z0-9]/g, '');
            if (normalizedFormKey === searchKey && formValue !== undefined && formValue !== null && formValue !== '') {
              value = formValue;
              originalKey = formKey;
              break;
            }
          }
        }

        if (shouldHideField(normalizedFieldKey, value)) return;

        // Mark as used
        if (originalKey) {
          usedFormKeys.add(originalKey.toLowerCase());
          usedFormKeys.add(toLowercase(originalKey));
        }
        usedFormKeys.add(normalizedFieldKey);

        // Check if field was changed - use consistent normalization
        const keyToCheck = (originalKey || normalizedFieldKey).toLowerCase().replace(/[^a-z0-9]/g, '');
        const isChanged = changedFieldKeys.has(keyToCheck);

        fieldsWithValues.push({
          fieldName: normalizedFieldKey,
          fieldLabel: fieldLabelName,
          value: formatDisplayValue(value),
          isChanged
        });
      });

      if (fieldsWithValues.length > 0) {
        result.push({
          moduleName,
          fields: fieldsWithValues
        });
      }
    });

    // Note: Authorized Contacts are now handled in the module loop above

    return result;
  }, [allModules, availableModules, fieldsMap, mergedFormData, changedFieldKeys, updatedFields]);

  // Redirect if not valid user (only after session is ready)
  useEffect(() => {
    if (sessionReady && !isExistingUser && !isNewUser) {
      logger.log('[Review] Not a valid user, redirecting to /');
      navigate('/');
    }
  }, [isExistingUser, isNewUser, sessionReady, navigate]);

  useEffect(() => {
    activityLogger.stepStarted(...REVIEW_STEP);
  }, []);

  // Handle submission
  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setError(null);
    activityLogger.stepCompleted(...REVIEW_STEP);
    activityLogger.submitStarted();

    try {
      // Submit data - returns submissionId for new users
      const submitResult = await submitUpdates();
      const newSubmissionId = submitResult?.submissionId || null;

      try {
        const firstName = mergedFormData.firstname || mergedFormData.firstName || '';
        const lastName = mergedFormData.lastname || mergedFormData.lastName || '';
        const patientName = [firstName, lastName].filter(Boolean).join(' ');
        activityLogger.setContext({ patientName });

        const painLevels = {
          current: mergedFormData.currentpainlevel || 0,
          worst: mergedFormData.worstpainlevel || 0,
          best: mergedFormData.bestpainlevel || 0
        };

        const sortedModules = (allModules || []).sort((a, b) => (a.count || 0) - (b.count || 0));

        const pdfBlob = await generatePDF(painLevels, mergedFormData, sortedModules, fieldsMap, {
          isFollowUp: isExistingUser,
          patientName,
          logoUrl: tenant?.logoUrl || null
        });

        const painDiagramBlob = updatedFields.painDiagramPdf instanceof Blob ? updatedFields.painDiagramPdf : null;

        if (pdfBlob) {
          // Pass newSubmissionId for new users (state might not have updated yet)
          await generateFollowUpPDFApi(pdfBlob, painDiagramBlob, newSubmissionId);
        }
      } catch (pdfErr) {
        logger.error('PDF generation error:', pdfErr?.message);
        activityLogger.submitPdfError(pdfErr || {});
      }

      activityLogger.submitCompleted();

      navigate('/success', {
        state: {
          changes: changedFieldKeys.size,
          patientName: submissionInfo?.patientInfo?.firstName || userEmail,
          isNewUser,
        },
      });
    } catch (err) {
      activityLogger.submitApiError(err);
      setError(err.message || 'Failed to submit');
    } finally {
      setIsSubmitting(false);
    }
  }, [submitUpdates, changedFieldKeys, submissionInfo, navigate, setError, userEmail, isNewUser, mergedFormData, allModules, fieldsMap, isExistingUser, updatedFields, generateFollowUpPDFApi]);

  const handleBack = () => {
    // Check if user has new issues (came from module selection -> edit flow)
    // or no new issues (came directly from new-issues page)
    const hasNewIssues = updatedFields?.hasnewissues === 'Yes' || updatedFields?.newissues;
    const hasSelectedModules = updatedFields && Object.keys(updatedFields).length > 3; // More than just new issues fields

    if (isExistingUser && !hasSelectedModules) {
      // User came directly from new-issues page (no modules edited)
      navigate('/new-issues');
    } else {
      // User went through edit flow
      navigate('/edit');
    }
  };

  // Show loading while session is being restored
  if (!sessionReady) {
    return (
      <div>
        <div className="logo-img d-flex-center">
          <div style={{ textAlign: 'center' }}>
            <img src={logoToDisplay} alt={tenant?.brandName || 'Logo'} style={{ width: '200px', height: 'auto', objectFit: 'contain', marginTop: '10px' }} />
          </div>
        </div>
        <div className="form-container" style={{ maxWidth: '800px', margin: '20px auto', padding: '20px' }}>
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div className="followup-spinner"></div>
            <p style={{ color: '#6c757d', fontSize: '14px' }}>Loading your review...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isExistingUser && !isNewUser) return null;

  return (
    <div style={{ background: '#f5f7fa', minHeight: '100vh', paddingBottom: '20px' }}>
      {/* Logo */}
      <div className="logo-img d-flex-center">
        <div style={{ textAlign: 'center' }}>
          <img src={logoToDisplay} alt={tenant?.brandName || 'Logo'} style={{ width: '160px', height: 'auto', objectFit: 'contain', marginTop: '8px' }} />
        </div>
      </div>

      {/* Form Container - Compact */}
      <div className="form-container" style={{
        maxWidth: '900px',
        margin: '10px auto',
        padding: '15px',
        background: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>

        {/* Title - Compact */}
        <h2 style={{
          textAlign: 'center',
          fontSize: '18px',
          fontWeight: '600',
          color: '#1a73e8',
          marginBottom: '12px',
          paddingBottom: '10px',
          borderBottom: '2px solid #1a73e8'
        }}>
          {isNewUser ? 'Review your submission' : 'Review your information'}
        </h2>

        {/* Yellow highlight legend — only show when updated fields exist */}
        {changedFieldKeys.size > 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            marginBottom: '12px',
            fontSize: '11px',
            color: '#666'
          }}>
            <span style={{
              display: 'inline-block',
              width: '14px',
              height: '14px',
              background: '#fffde7',
              border: '1px solid #ffc107',
              borderRadius: '2px'
            }}></span>
            <span>= Updated fields</span>
          </div>
        )}

        {error && (
          <div style={{
            padding: '8px 12px',
            borderRadius: '4px',
            marginBottom: '12px',
            background: '#fee',
            color: '#c33',
            fontSize: '12px',
            border: '1px solid #fcc'
          }}>
            {error}
          </div>
        )}

        {/* New Issues Section — show only when patient typed new issues THIS session */}
        {isExistingUser && updatedFields?.hasnewissues === 'Yes' && updatedFields?.newissues && (
          <div style={{
            background: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '6px',
            marginBottom: '12px',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '8px 12px',
              fontWeight: '600',
              fontSize: '12px',
              color: '#856404',
              background: '#ffeeba'
            }}>
              New Issues Reported
            </div>
            <div style={{
              padding: '8px 12px',
              fontSize: '12px',
              color: '#333',
              whiteSpace: 'pre-wrap'
            }}>
              {updatedFields.newissues}
            </div>
          </div>
        )}

        {/* Compact Data Preview */}
        <div style={{
          border: '1px solid #e0e0e0',
          borderRadius: '6px',
          overflow: 'hidden'
        }}>
          {organizedData.map((module, moduleIndex) => (
            <div key={moduleIndex}>
              {/* Section Header - Compact with Update button */}
              <div style={{
                padding: '8px 12px',
                fontWeight: '600',
                fontSize: '13px',
                color: '#fff',
                background: '#1a73e8',
                borderTop: moduleIndex > 0 ? '1px solid #1557b0' : 'none',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <span>{module.moduleName}</span>
                {isExistingUser && (
                  <button
                    onClick={() => {
                      const moduleId = module.moduleName?.toLowerCase().replace(/\s+/g, '_');
                      navigate(`/update/${moduleId}`);
                    }}
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      border: '1px solid rgba(255,255,255,0.5)',
                      color: '#fff',
                      padding: '3px 12px',
                      borderRadius: '4px',
                      fontSize: '11px',
                      fontWeight: '500',
                      cursor: 'pointer',
                    }}
                  >
                    Update
                  </button>
                )}
              </div>

              {/* Fields - Compact 2-column grid for desktop */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: '0'
              }}>
                {module.fields.map((field, fieldIndex) => {
                  // Header row for contacts - spans full width
                  if (field.isHeader) {
                    return (
                      <div
                        key={fieldIndex}
                        style={{
                          gridColumn: '1 / -1',
                          padding: '8px 12px',
                          background: '#f0f4f8',
                          borderBottom: '1px solid #e0e0e0',
                          fontWeight: '600',
                          fontSize: '12px',
                          color: '#1a73e8'
                        }}
                      >
                        {toDisplayLabel(field.fieldLabel)}
                      </div>
                    );
                  }

                  return (
                    <div
                      key={fieldIndex}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '6px 12px',
                        borderBottom: field.isChanged ? '2px solid #f5d56a' : '1px solid #f0f0f0',
                        borderRight: field.isChanged ? '2px solid #f5d56a' : '1px solid #f0f0f0',
                        borderLeft: field.isChanged ? '2px solid #f5d56a' : 'none',
                        borderTop: field.isChanged ? '2px solid #f5d56a' : 'none',
                        background: field.isChanged ? '#fff3c4' : '#fff',
                        minHeight: '40px'
                      }}
                    >
                      <div style={{
                        fontSize: '10px',
                        color: '#666',
                        fontWeight: '500',
                        marginBottom: '2px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px'
                      }}>
                        {toDisplayLabel(field.fieldLabel)}
                      </div>
                      <div style={{
                        fontSize: '13px',
                        color: '#333',
                        fontWeight: field.isChanged ? '600' : '400',
                        wordBreak: 'break-word'
                      }}>
                        {/* Render links for ID Verification URLs */}
                        {field.isBase64Image && field.base64Src ? (
                          <img
                            src={field.base64Src}
                            alt={toDisplayLabel(field.fieldLabel)}
                            style={{ width: '100%', maxWidth: '360px', height: 'auto', borderRadius: '6px', border: '1px solid #ddd', marginTop: '4px' }}
                          />
                        ) : field.isLink && field.value ? (
                          <a
                            href={field.value}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1a73e8', textDecoration: 'underline' }}
                          >
                            View Document
                          </a>
                        ) : (
                          field.value || '-'
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Action Buttons - Compact */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '20px',
          gap: '12px'
        }}>
          <button
            type="button"
            onClick={() => navigate('/new-issues')}
            disabled={isSubmitting}
            style={{
              padding: '10px 20px',
              fontSize: '13px',
              fontWeight: '500',
              border: '1px solid #ccc',
              background: '#fff',
              color: '#666',
              borderRadius: '6px',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
          >
            Back
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSubmitting}
            style={{
              padding: '10px 24px',
              fontSize: '13px',
              fontWeight: '600',
              border: 'none',
              background: isSubmitting ? '#ccc' : '#1a73e8',
              color: '#fff',
              borderRadius: '6px',
              cursor: isSubmitting ? 'not-allowed' : 'pointer',
              minWidth: '140px',
              transition: 'all 0.2s',
              boxShadow: '0 2px 4px rgba(26,115,232,0.3)'
            }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FollowUpReview;
